import { resolveLocale } from './calendar'

/** A time of day as forms and the API hold it, `HH:mm`. */
export type TimeOfDay = string

/** Minutes since midnight of an `HH:mm` or `HH:mm:ss` value, or `null` for anything else. */
export function minutesOf(value: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/u.exec(value)
  return match === null ? null : Number(match[1]) * 60 + Number(match[2])
}

/** `HH:mm` for minutes since midnight. */
export function timeOf(minutes: number): TimeOfDay {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

/** One formatter per locale, since building one is the slow part. */
const formatters = new Map<string, Intl.DateTimeFormat>()

/** Hours and minutes as `locale` writes them: 12- or 24-hour, as its people expect. */
export function timeFormat(locale: string | undefined): Intl.DateTimeFormat {
  const key = resolveLocale(locale) ?? ''
  let formatter = formatters.get(key)
  if (formatter === undefined) {
    // UTC, so the minutes given are the minutes shown in every time zone.
    formatter = new Intl.DateTimeFormat(key === '' ? undefined : key, { timeStyle: 'short', timeZone: 'UTC' })
    formatters.set(key, formatter)
  }
  return formatter
}

/** Minutes since midnight as `locale` writes them, such as "5:30 PM" or "17:30". */
export function formatTime(minutes: number, locale: string | undefined): string {
  return timeFormat(locale).format(Date.UTC(2000, 0, 1, Math.floor(minutes / 60), minutes % 60))
}

/** Text as it is compared: lower case, without spaces, direction marks or the dots of a word, so "P.M." is "pm" and "17.30" stays. */
function squeezed(text: string): string {
  return text.toLowerCase().replace(/[\s\p{Cf}]/gu, '').replace(/\.(?=\p{L})|(?<=\p{L})\./gu, '')
}

/** `text` with `locale`'s own digits, such as Arabic-Indic ones, as ASCII digits. */
function asciiDigits(text: string, locale: string | undefined): string {
  const digits = new Intl.NumberFormat(resolveLocale(locale), { useGrouping: false })
  const own = new Map(Array.from({ length: 10 }, (_, digit) => [digits.format(digit), String(digit)]))
  return text.replace(/\p{Nd}/gu, (digit) => own.get(digit) ?? digit)
}
/** The words without blanks or repeats, longest first, so "am" is taken whole rather than its "a". */
function byLength(words: readonly string[]): readonly string[] {
  return [...new Set(words.filter((word) => word !== ''))].toSorted((one, other) => other.length - one.length)
}

/** The words `locale` marks morning and afternoon with, such as "午前" and "午後", and the Latin ones. */
function periods(locale: string | undefined): { readonly am: readonly string[]; readonly pm: readonly string[] } {
  const twelveHour = new Intl.DateTimeFormat(resolveLocale(locale), { hour: 'numeric', hour12: true, timeZone: 'UTC' })
  const period = (hour: number) => squeezed(twelveHour.formatToParts(Date.UTC(2000, 0, 1, hour)).find((part) => part.type === 'dayPeriod')?.value ?? '')
  return { am: byLength([period(1), 'am', 'a']), pm: byLength([period(13), 'pm', 'p']) }
}

/**
 * The time a person typed, as `HH:mm`, or `null` when it is not one. It takes the 24-hour clock
 * ("17:30", "1730", "17.30", "17h30") and the 12-hour one with a morning or afternoon word before
 * or after the time, in Latin letters or `locale`'s own ("5:30 pm", "5p", "午後5時30分").
 */
export function parseTime(text: string, locale: string | undefined): TimeOfDay | null {
  let rest = asciiDigits(squeezed(text), locale)
  let period: 'am' | 'pm' | null = null
  const words = periods(locale)
  for (const [name, list] of [['pm', words.pm], ['am', words.am]] as const) {
    const word = list.find((candidate) => rest.startsWith(candidate) || rest.endsWith(candidate))
    if (word !== undefined && period === null) {
      period = name
      rest = rest.startsWith(word) ? rest.slice(word.length) : rest.slice(0, -word.length)
    }
  }
  // Hours alone, hours and minutes run together ("930", "1730"), or split by a time mark ("17:30",
  // "17h30", "5時30分", "5시30분", "5点30分"); anything else, such as a second period word, is not a time.
  const match = /^(\d{1,2})(?:[:.h時时시点點](?:(\d{2})[分분]?)?)?$/u.exec(rest) ?? /^(\d{1,2})(\d{2})$/u.exec(rest)
  if (match === null) {
    return null
  }
  let hours = Number(match[1])
  const minutes = Number(match[2] ?? 0)
  if (minutes > 59) {
    return null
  }
  if (period === null) {
    return hours > 23 ? null : timeOf(hours * 60 + minutes)
  }
  if (hours < 1 || hours > 12) {
    return null
  }
  hours = (hours % 12) + (period === 'pm' ? 12 : 0)
  return timeOf(hours * 60 + minutes)
}
